<script setup lang="ts">
import { getSiteList, getStructureList } from "@carnet/api/backoffice";
import type { SiteResource, StructureResource } from "@carnet/api/backoffice";
import { framingBbox } from "../utils/geo";
import type { LocationFilter } from "../utils/location";
import { apiRadiusKm, DEFAULT_RADIUS_OPTIONS, locationInputKey } from "../utils/location";
import type { MapboxRetrieved } from "../composables/useAppMapboxSearch";
import AppInputLocation, { type InputLocationExtraGroup, type InputLocationExtraItem } from "./AppInputLocation.vue";

type FieldSize = "md" | "lg" | "xl";

const MAPBOX_ZONE_LIMIT = 4;

const props = withDefaults(
  defineProps<{
    withRadius?: boolean;
    radiusOptions?: { label: string; value: number }[];
    defaultRadius?: number;
    placeholder?: string;
    context?: "sites" | "structures";
    withPickButton?: boolean;
    // Ajoute une entrée finale « Utiliser "<recherche>" comme nom du site » (context sites) qui
    // émet `select:custom-name` : le tunnel de création s'en sert pour saisir un customSiteName libre.
    withCustomName?: boolean;
    size?: FieldSize;
    // Consentement Mapbox injecté par l'hôte (défaut : autorisé).
    mapboxAllowed?: boolean;
    // Plateforme mobile (orientation verticale champ + rayon). Web : valeur responsive injectée.
    isMobile?: boolean;
    // Recherche locale injectée (offline) : si fournie, remplace l'appel réseau getSiteList /
    // getStructureList. Le mobile l'alimente depuis le cache Dexie (MiniSearch) ; le web l'omet.
    searchSites?: (q: string) => SiteResource[] | Promise<SiteResource[]>;
    searchStructures?: (q: string) => StructureResource[] | Promise<StructureResource[]>;
  }>(),
  {
    withRadius: false,
    radiusOptions: () => DEFAULT_RADIUS_OPTIONS,
    defaultRadius: 1,
    placeholder: undefined,
    context: undefined,
    withPickButton: false,
    withCustomName: false,
    size: "xl",
    mapboxAllowed: true,
    isMobile: false,
    searchSites: undefined,
    searchStructures: undefined,
  }
);

const filter = defineModel<LocationFilter>("filter", { default: () => ({}) });

const emit = defineEmits<{
  "select:site": [site: SiteResource];
  "select:structure": [structure: StructureResource];
  "select:custom-name": [name: string];
}>();

// Longueur minimale du terme avant de proposer l'entrée « nom de site libre » (aligné sur le
// seuil d'ouverture du dropdown d'AppInputLocation).
const CUSTOM_NAME_MIN_LENGTH = 2;

const { t } = useI18n();

const orientation = computed<"horizontal" | "vertical">(() => (props.isMobile ? "vertical" : "horizontal"));

const LocationInput = inject(locationInputKey, AppInputLocation);

const inputLocationRef = useTemplateRef<{ clearSearch: () => void }>("inputLocation");

const locationModel = computed<string>({
  get: () => filter.value.location?.name ?? "",
  set: (v) => {
    if (!v) filter.value = {};
    // sinon: ignoré — la mise à jour complète (location, center, bbox) arrive via @select
  },
});

const radius = ref(filter.value.radius ?? props.defaultRadius);

// Dernier terme saisi (remonté par AppInputLocation) : alimente l'entrée « nom de site libre ».
const searchTerm = ref("");

watch(
  () => filter.value.radius,
  (r) => {
    if (r !== undefined) radius.value = r;
  }
);

const {
  results: backofficeResults,
  loading: backofficeLoading,
  search: searchBackoffice,
  clear: clearBackoffice,
} = useAppDebouncedSearch<SiteResource | StructureResource>(
  async (q) => {
    if (props.context === "sites") {
      if (props.searchSites) return props.searchSites(q);
      const res = await getSiteList({ composable: "$fetch", path: { locale: appLocale() }, query: { terms: q, size: 4 } });
      return (res as unknown as { data: { list?: SiteResource[] } }).data?.list ?? [];
    }
    if (props.searchStructures) return props.searchStructures(q);
    const res = await getStructureList({ composable: "$fetch", path: { locale: appLocale() }, query: { terms: q, size: 4 } });
    return (res as unknown as { data: { list?: StructureResource[] } }).data?.list ?? [];
  },
  { minLength: 3 }
);

function onMapboxSelect(loc: MapboxRetrieved) {
  const base = { location: loc, center: loc.center, radius: props.withRadius ? radius.value : undefined };
  filter.value = { ...base, bbox: framingBbox(loc.bbox, loc.center, apiRadiusKm(base)) };
  clearBackoffice();
}

const extraGroups = computed<InputLocationExtraGroup[]>(() => {
  if (!props.context) return [];

  const groups: InputLocationExtraGroup[] = [];

  if (props.context === "sites") {
    const items: InputLocationExtraItem[] = (backofficeResults.value as SiteResource[]).map((result) => ({
      value: String(result.id),
      label: result.name,
      description: [t("location.search.site_type"), result.nearbyCityLabel].filter(Boolean).join(" • "),
      ...(props.withPickButton ? { trailingLabel: t("ui.pick") } : { icon: "icon:arrowRight" }),
      onSelect: () => {
        emit("select:site", result);
        nextTick(() => inputLocationRef.value?.clearSearch());
      },
    }));
    if (items.length) groups.push({ label: t("location.search.sites"), items });

    // Entrée finale (groupe sans libellé) : réutilise le texte recherché comme nom de site libre.
    const term = searchTerm.value.trim();
    if (props.withCustomName && term.length >= CUSTOM_NAME_MIN_LENGTH) {
      groups.push({
        items: [
          {
            value: `custom-name:${term}`,
            label: t("location.search.use_custom_name", { name: term }),
            icon: "icon:chevronRight",
            onSelect: () => {
              emit("select:custom-name", term);
              nextTick(() => inputLocationRef.value?.clearSearch());
            },
          },
        ],
      });
    }
  } else {
    const items: InputLocationExtraItem[] = (backofficeResults.value as StructureResource[]).map((result) => ({
      value: String(result.id),
      label: result.name,
      description: [t("location.search.structure_type"), result.type.label].filter(Boolean).join(" • "),
      icon: "icon:marker",
      onSelect: () => {
        emit("select:structure", result);
        nextTick(() => inputLocationRef.value?.clearSearch());
      },
    }));
    if (items.length) groups.push({ label: t("location.search.structures"), items });
  }

  return groups;
});

const mapboxGroupLabel = computed(() => (props.context ? t("location.search.places") : undefined));

function onSearchTerm(q: string) {
  searchTerm.value = q;
  if (props.context) searchBackoffice(q);
  else clearBackoffice();
}

watch(radius, (r) => {
  if (!props.withRadius) return;
  const loc = filter.value.location;
  if (!loc || !filter.value.center) return;
  if (filter.value.radius === r) return;
  const base = { location: loc, center: filter.value.center, radius: r };
  filter.value = { ...base, bbox: framingBbox(loc.bbox, filter.value.center, apiRadiusKm(base)) };
});
</script>

<template>
  <UFieldGroup color="primary" :size="size" :orientation="orientation">
    <component
      :is="LocationInput"
      ref="inputLocation"
      v-model="locationModel"
      :placeholder="placeholder"
      :size="size"
      :limit="MAPBOX_ZONE_LIMIT"
      boost-broad-features
      :extra-groups="extraGroups"
      :extra-loading="backofficeLoading"
      :mapbox-group-label="mapboxGroupLabel"
      :mapbox-allowed="mapboxAllowed"
      class="grow"
      @select="onMapboxSelect"
      @search="onSearchTerm"
      highlight
    />
    <USelect
      v-if="withRadius"
      v-model="radius"
      :items="radiusOptions"
      name="radius"
      highlight
      class="w-auto text-right"
      :content="{ position: 'popper', align: 'end' }"
      :ui="{
        base: 'aria-expanded:bg-primary-700 aria-expanded:text-white hover:not-disabled:bg-blue-300 max-sm:py-2',
        placeholder: 'in-aria-expanded:text-white',
      }"
    />
  </UFieldGroup>
</template>
